/**
 * English (en) translation resources — one statically-imported bundle per
 * namespace, exported as a single record AND registered into the shared i18next
 * instance as an import side effect.
 *
 * TEST-GRAPH CONTRACT (WI 151e3bed, extending WI d36e25d5's contract to English).
 * These 46 JSON files used to be imported directly by config.ts. config.ts is
 * reachable from `shared/utils/errors.ts` and `shared/auth/index.ts`, which
 * virtually every component imports — so editing ONE English locale JSON put
 * 1546 of 1899 test files into merge-and-ship's `vitest --changed` impact set
 * (81% of the corpus, p50 12-14 minutes). Almost none of them can observe the
 * change: the dependency is on translated DATA behind a singleton, not on code.
 *
 * So product code must reach this module through EXACTLY ONE static edge — the
 * `import './i18n/resourcesEn'` at the top of src/index.tsx, which no test
 * imports. Tests get it from src/setupTests.ts through a COMPUTED dynamic
 * import, which vitest's `--changed` graph cannot record (see
 * vitest.config.ts's dynamicImportVarsOptions.exclude). Result: an English
 * locale-only diff selects its true importers (~4 i18n tests) instead of 1546.
 *
 * Do NOT add a static import of this module from anywhere else in the app graph,
 * and do not "simplify" setupTests.ts's computed import to a literal one — either
 * re-links all 46 English JSONs into the test graph and re-inflates the gate.
 * tools/check_vitest_impact_hubs.py fails the build if you do.
 *
 * The file is deliberately NOT named `resources.en.ts`: config.ts's lazy loader
 * globs `./resources.*.ts`, and matching that pattern would emit a second, never-
 * fetched copy of every English bundle as a lazy chunk. English is eager.
 */

import { registerEnglishBundles, type LanguageResourceBundles } from './config';

import commonEn from './locales/en/common.json';
import authEn from './locales/en/auth.json';
import tasksEn from './locales/en/tasks.json';
import userManagementEn from './locales/en/user_management.json';
import accountingEn from './locales/en/accounting.json';
import fileManagementEn from './locales/en/file_management.json';
import aiAgentsEn from './locales/en/aiAgents.json';
import pipelineEn from './locales/en/pipeline.json';
import emailEn from './locales/en/email.json';
import adminEn from './locales/en/admin.json';
import aiIntakeEn from './locales/en/ai_intake.json';
import sysadminEn from './locales/en/sysadmin.json';
import codingEn from './locales/en/coding.json';
import developerEn from './locales/en/developer.json';
import recruitingEn from './locales/en/recruiting.json';
import careersEn from './locales/en/careers.json';
import peopleEn from './locales/en/people.json';
import aiChatEn from './locales/en/aiChat.json';
import messagingEn from './locales/en/messaging.json';
import errorsEn from './locales/en/errors.json';
import messagesEn from './locales/en/messages.json';
import avatarsEn from './locales/en/avatars.json';
import productsEn from './locales/en/products.json';
import contactsEn from './locales/en/contacts.json';
import supportEn from './locales/en/support.json';
import externalEn from './locales/en/external.json';
import designSystemEn from './locales/en/design_system.json';
import websiteHostingEn from './locales/en/websiteHosting.json';
import shipmentsEn from './locales/en/shipments.json';
import customsEn from './locales/en/customs.json';
import formsBpmEn from './locales/en/formsBpm.json';
import remindersEn from './locales/en/reminders.json';
import inventoryEn from './locales/en/inventory.json';
import procurementEn from './locales/en/procurement.json';
import customFieldsEn from './locales/en/custom_fields.json';
import softwareFactoryEn from './locales/en/softwareFactory.json';
import notificationsEn from './locales/en/notifications.json';
import monitoringEn from './locales/en/monitoring.json';
import marketingEn from './locales/en/marketing.json';
import contractsEn from './locales/en/contracts.json';
import salesEn from './locales/en/sales.json';
import brokerageEn from './locales/en/brokerage.json';
import billingEn from './locales/en/billing.json';
import chatbotEn from './locales/en/chatbot.json';
import onboardingEn from './locales/en/onboarding.json';
import whatsappEn from './locales/en/whatsapp.json';

// Annotated with the SAME exact Record<I18nNamespace, …> the lazy languages use
// (resources.es.ts / .he.ts / .zh.ts). Without it, adding a namespace to
// ALL_NAMESPACES fails tsc for es/he/zh while English compiles clean and silently
// omits it — and a missing English bundle renders the raw key in ALL four languages,
// past a green build, because 'en' is the fallbackLng.
const englishResources: LanguageResourceBundles = {
  common: commonEn,
  auth: authEn,
  tasks: tasksEn,
  user_management: userManagementEn,
  accounting: accountingEn,
  file_management: fileManagementEn,
  aiAgents: aiAgentsEn,
  pipeline: pipelineEn,
  email: emailEn,
  admin: adminEn,
  ai_intake: aiIntakeEn,
  sysadmin: sysadminEn,
  coding: codingEn,
  developer: developerEn,
  recruiting: recruitingEn,
  careers: careersEn,
  people: peopleEn,
  aiChat: aiChatEn,
  messaging: messagingEn,
  errors: errorsEn,
  messages: messagesEn,
  avatars: avatarsEn,
  products: productsEn,
  contacts: contactsEn,
  support: supportEn,
  external: externalEn,
  design_system: designSystemEn,
  websiteHosting: websiteHostingEn,
  shipments: shipmentsEn,
  customs: customsEn,
  formsBpm: formsBpmEn,
  reminders: remindersEn,
  inventory: inventoryEn,
  procurement: procurementEn,
  custom_fields: customFieldsEn,
  softwareFactory: softwareFactoryEn,
  notifications: notificationsEn,
  monitoring: monitoringEn,
  marketing: marketingEn,
  contracts: contractsEn,
  sales: salesEn,
  brokerage: brokerageEn,
  billing: billingEn,
  chatbot: chatbotEn,
  onboarding: onboardingEn,
  whatsapp: whatsappEn,
};

// Side effect on import: config.ts initialises i18next with an empty English
// bundle set (it can no longer import these JSONs itself without recreating the
// edge above), so the bundles are registered here, before anything renders.
registerEnglishBundles(englishResources);

export default englishResources;
